// `GET /api/v1/runs`: the landing page's data (#231, over #230's route).
//
// **The route is #230's and is not on `main` as this is written.** #231's brief
// said to read the list route's response shape off
// `control_plane/routes/records.py` rather than guess it; the file holds the
// single-run route (#213) and no list route, and #230 is open and unassigned to
// this session. So the contract below is taken from the next-best statement of
// it — #230's own text, which names the row's fields exactly:
//
//   > `run_id`, `status`, `dispatched_at`, `finished_at`, `last_seq`
//   > the incident's `title` and `fingerprint`, so a row is readable without a
//   > second call
//   > `conclusion` is **not** in the list
//
// and the paging it names: `?limit=` (default 50, max 200) and `?before=`
// taking the previous page's oldest `dispatched_at`. Every field name here is
// one of those or one `records.py` already answers with.
//
// The one thing #230's text does not state is the envelope — whether the body
// is the array or an object holding it. Its test's name says another workspace's
// key "gets `[]`", which reads as the bare array, but it is not said in prose,
// so `runRowsFrom` reads both and nothing else. When the route lands, delete
// the branch it did not choose.
//
// **`title` may be absent.** The `incident` table has no `title` column
// (`store/migrations/0002_incidents_and_runs.sql`); the column of that name is
// the `alert`'s. Whatever #230 joins it from, a row whose title is null is a
// row this page must still render, so the type says `string | null` and
// `runLabel` falls back through the fingerprint to the id.

import type { Api } from "./http";
import { ticketsFrom, type RunTicket } from "./runRecord";

export const RUNS_PATH = "/api/v1/runs";

/** One row of the list. Not the run record: `conclusion` is deliberately absent. */
export interface RunRow {
  run_id: string;
  status: string;
  /** Closed is shown as Resolved; every other incident state is Unresolved. */
  incident_status?: string;
  /** When the control plane enqueued the run — the run row's `started_at`. */
  dispatched_at: string | null;
  /** When Finalization closed it, or null while it is open. */
  finished_at: string | null;
  last_seq: number;
  title: string | null;
  fingerprint: string | null;
  /** The incident's tickets, as the record carries them (#818). */
  tickets?: RunTicket[];
  /** The AWS account the run investigated, and what the workspace calls it (#823, #811). */
  account_id?: string | null;
  account_name?: string | null;
  /** The source the incident's first alert came in on, and the sender's kind (#811). */
  source_name?: string | null;
  source_type?: string | null;
  /** Opened by "Send test alert" on a source (#876): a test, which costs no credits. */
  test?: boolean;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function asRow(value: unknown): RunRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.run_id !== "string") return null;
  return {
    run_id: row.run_id,
    status: typeof row.status === "string" ? row.status : "unknown",
    incident_status: typeof row.incident_status === "string" ? row.incident_status : "unknown",
    dispatched_at: typeof row.dispatched_at === "string" ? row.dispatched_at : null,
    finished_at: typeof row.finished_at === "string" ? row.finished_at : null,
    last_seq: typeof row.last_seq === "number" ? row.last_seq : 0,
    title: typeof row.title === "string" && row.title !== "" ? row.title : null,
    fingerprint: typeof row.fingerprint === "string" ? row.fingerprint : null,
    tickets: ticketsFrom(row.tickets),
    account_id: text(row.account_id),
    account_name: text(row.account_name),
    source_name: text(row.source_name),
    source_type: text(row.source_type),
    test: row.test === true,
  };
}

/** The rows out of the body, whichever of the two envelopes #230 answers with. */
export function runRowsFrom(body: unknown): RunRow[] {
  const list = Array.isArray(body)
    ? body
    : typeof body === "object" && body !== null && Array.isArray((body as { runs?: unknown }).runs)
      ? ((body as { runs: unknown[] }).runs)
      : null;
  if (list === null) return [];
  const rows: RunRow[] = [];
  for (const value of list) {
    const row = asRow(value);
    if (row !== null) rows.push(row);
  }
  return rows;
}

/**
 * Newest first, by when the run was dispatched.
 *
 * #230 answers in this order already. The page states it anyway, because the
 * order a list is read in is the page's own claim: a payload that arrives out
 * of order, or two pages merged, must not render mixed. A row with no
 * `dispatched_at` sorts last rather than first — an unknown clock is not the
 * newest thing on the page.
 */
export function newestFirst(rows: readonly RunRow[]): RunRow[] {
  const at = (row: RunRow) => (row.dispatched_at === null ? "" : row.dispatched_at);
  return [...rows].sort((left, right) => at(right).localeCompare(at(left)));
}

/** What a row is called: the incident's title, else its fingerprint, else the id. */
export function runLabel(row: RunRow): string {
  return row.title ?? row.fingerprint ?? row.run_id;
}

export async function listRuns(
  api: Api,
  page: { limit?: number; before?: string } = {},
): Promise<RunRow[]> {
  const query = new URLSearchParams();
  if (page.limit !== undefined) query.set("limit", String(page.limit));
  if (page.before !== undefined) query.set("before", page.before);
  const search = query.toString();
  const suffix = search === "" ? "" : `?${search}`;
  return newestFirst(runRowsFrom(await api.get<unknown>(`${RUNS_PATH}${suffix}`)));
}
